import React, { useMemo } from 'react';

interface BeautifiedGuideRendererProps {
  htmlContent: string;
}

const BeautifiedGuideRenderer: React.FC<BeautifiedGuideRendererProps> = ({ htmlContent }) => {
  
  const processedHtml = useMemo(() => {
    if (!htmlContent) return '';
    
    let html = htmlContent;

    html = html.replace(/style="[^"]*"/g, '');

    html = html.replace(
      /━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━/g,
      '<hr class="guide-divider" />'
    );

    html = html.replace(
      /📄\s*(P\d+[:：]\s*[^\n<]+)/g,
      '<h1 class="guide-main-title">📄 $1</h1>'
    );

    html = html.replace(
      /\*\(内部坐标系[：:]\s*([^\)]+)\)\*/g,
      '<p class="guide-coordinate">📍 $1</p>'
    );

    html = html.replace(
      /-\s*\*\*建议讲解时长\*\*[：:]\s*([^\n<]+)/g,
      '<span class="guide-duration duration-teach">⏱️ 讲解：$1</span>'
    );
    html = html.replace(
      /-\s*\*\*建议互动时长\*\*[：:]\s*([^\n<]+)/g,
      '<span class="guide-duration duration-interact">💬 互动：$1</span>'
    );

    html = html.replace(
      /###\s*1️⃣\s*教师操作/g,
      '<h2 class="guide-section section-action"><span class="section-icon">👨‍🏫</span> 教师操作</h2>'
    );
    html = html.replace(
      /###\s*2️⃣\s*教师讲解逐字稿/g,
      '<h2 class="guide-section section-script"><span class="section-icon">🗣️</span> 教师讲解逐字稿</h2>'
    );
    html = html.replace(
      /###\s*3️⃣\s*学生活动与预期反馈/g,
      '<h2 class="guide-section section-student"><span class="section-icon">🙋‍♂️</span> 学生活动与预期反馈</h2>'
    );
    html = html.replace(
      /###\s*4️⃣\s*教学要点解析/g,
      '<h2 class="guide-section section-points"><span class="section-icon">💡</span> 教学要点解析</h2>'
    );

    html = html.replace(
      />\s*\*\[([^\]]+)\]\*</g,
      '<blockquote class="guide-hint">$1</blockquote>'
    );

    html = html.replace(
      /`([^`]+)`/g,
      '<code class="guide-pause">$1</code>'
    );

    html = html.replace(
      /-\s*\*\*设计意图\*\*[：:]/g,
      '<p class="guide-point point-intent"><strong>🎯 设计意图：</strong></p>'
    );
    html = html.replace(
      /-\s*\*\*对应目标\*\*[：:]/g,
      '<p class="guide-point point-goal"><strong>📌 对应目标：</strong></p>'
    );
    html = html.replace(
      /-\s*\*\*具体动作\*\*[：:]/g,
      '<p class="guide-point point-action"><strong>🎬 具体动作：</strong></p>'
    );
    html = html.replace(
      /-\s*\*\*预期反应\*\*[：:]/g,
      '<p class="guide-point point-reaction"><strong>✨ 预期反应：</strong></p>'
    );
    html = html.replace(
      /-\s*\*\*防冷场预案\*\*[：:]/g,
      '<p class="guide-point point-backup"><strong>🛡️ 防冷场预案：</strong></p>'
    );

    return html;
  }, [htmlContent]);

  return (
    <>
      <style>{`
        .nexus-guide-theme {
          font-family: 'Nunito', 'Microsoft YaHei', -apple-system, BlinkMacSystemFont, sans-serif;
          line-height: 1.8;
          color: #334155;
          font-size: 0.9rem;
          white-space: normal;
          word-wrap: break-word;
        }

        .nexus-guide-theme p {
          margin-bottom: 10px;
          line-height: 1.7;
          white-space: normal;
          word-wrap: break-word;
        }

        .nexus-guide-theme ol,
        .nexus-guide-theme ul {
          padding-left: 24px;
          margin-bottom: 16px;
        }
        .nexus-guide-theme li {
          margin-bottom: 8px;
          color: #475569;
          line-height: 1.7;
        }

        .guide-divider {
          border: none;
          height: 2px;
          background: linear-gradient(90deg, transparent, #e2e8f0 20%, #e2e8f0 80%, transparent);
          margin: 20px 0;
        }

        .guide-main-title {
          font-size: 1.3rem;
          font-weight: 700;
          color: #1e293b;
          text-align: center;
          margin: 16px 0;
          padding: 12px;
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
          border-radius: 12px;
          border: 1px solid #bae6fd;
        }

        .guide-coordinate {
          text-align: center;
          font-size: 0.85rem;
          color: #64748b;
          background: #f8fafc;
          padding: 8px 16px;
          border-radius: 20px;
          margin: 12px auto;
          display: inline-block;
          width: 100%;
          box-sizing: border-box;
        }

        .guide-duration {
          display: inline-block;
          font-size: 0.8rem;
          padding: 4px 12px;
          border-radius: 16px;
          margin-right: 8px;
          margin-bottom: 12px;
          font-weight: 500;
        }
        .duration-teach {
          background: #dbeafe;
          color: #1d4ed8;
        }
        .duration-interact {
          background: #dcfce7;
          color: #15803d;
        }

        .guide-section {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 1rem;
          font-weight: 700;
          padding: 10px 16px;
          border-radius: 10px;
          margin: 20px 0 12px 0;
          border-left: 4px solid;
        }
        .section-icon {
          font-size: 1.2rem;
        }
        .section-action {
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          border-color: #22c55e;
          color: #166534;
        }
        .section-script {
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          border-color: #3b82f6;
          color: #1e40af;
        }
        .section-student {
          background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
          border-color: #f97316;
          color: #c2410c;
        }
        .section-points {
          background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%);
          border-color: #a855f7;
          color: #7e22ce;
        }

        .guide-hint {
          background: #fefce8;
          border: 1px dashed #fbbf24;
          border-radius: 8px;
          padding: 10px 14px;
          margin: 12px 0;
          font-size: 0.85rem;
          color: #92400e;
          font-style: italic;
        }

        .guide-pause {
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          color: #92400e;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.85rem;
          font-weight: 500;
          white-space: nowrap;
        }

        .guide-point {
          padding: 10px 14px;
          border-radius: 8px;
          margin: 8px 0;
          font-size: 0.9rem;
        }
        .guide-point strong {
          display: block;
          margin-bottom: 4px;
        }
        .point-intent {
          background: #ecfdf5;
          border-left: 3px solid #10b981;
        }
        .point-goal {
          background: #fef3c7;
          border-left: 3px solid #f59e0b;
        }
        .point-action {
          background: #f0f9ff;
          border-left: 3px solid #0ea5e9;
        }
        .point-reaction {
          background: #fdf4ff;
          border-left: 3px solid #d946ef;
        }
        .point-backup {
          background: #fef2f2;
          border-left: 3px solid #ef4444;
        }

        .nexus-guide-theme blockquote:not(.guide-hint):not(.guide-point) {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 16px;
          margin: 12px 0;
          font-style: italic;
          color: #64748b;
        }

        .nexus-guide-theme img {
          max-width: 100%;
          border-radius: 8px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          margin: 12px 0;
        }

        .nexus-guide-theme strong {
          color: #1e293b;
        }
      `}</style>
      
      <div 
        className="nexus-guide-theme"
        dangerouslySetInnerHTML={{ __html: processedHtml }} 
      />
    </>
  );
};

export default BeautifiedGuideRenderer;
